/**
 * Materials Page
 *
 * Main page for managing raw materials
 * Features:
 * - List view with search and filtering
 * - Material category filtering (棒料, 紧固件, 耗材)
 * - Create/Edit material functionality
 * - Price display and management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus,
  RefreshCw,
  Search,
  Loader2,
  Boxes,
  Edit,
  Trash2,
  Eye,
  X,
  TrendingUp,
  AlertTriangle,
  Upload,
} from 'lucide-react';
import Layout from '../components/Layout';
import authService from '../services/authService';
import materialService from '../services/materialService';
import ContractImportDialog from '../components/ContractImportDialog';

function MaterialsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Load user info on mount
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  // Load materials
  const loadMaterials = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        page_size: pageSize,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (categoryFilter) {
        params.material_category = categoryFilter;
      }

      const response = await materialService.getMaterials(params);
      setMaterials(response.items || []);
      setTotalItems(response.total || 0);

      // Load low stock count
      try {
        const lowStockResponse = await materialService.getLowStockMaterials(10);
        setLowStockCount(lowStockResponse.total || 0);
      } catch {
        // Ignore low stock loading errors
      }
    } catch (err) {
      console.error('Failed to load materials:', err);
      setError('加载物料列表失败，请重试。');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, categoryFilter]);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials, refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleImportSuccess = () => {
    handleRefresh();
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleCategoryFilter = (value) => {
    setCategoryFilter(value === 'all' ? '' : value);
    setPage(1);
  };

  const handleCreateMaterial = () => {
    navigate('/materials/new');
  };

  const handleViewMaterial = (materialId) => {
    navigate(`/materials/${materialId}`);
  };

  const handleEditMaterial = (materialId) => {
    navigate(`/materials/${materialId}/edit`);
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('确定要删除此物料吗？')) {
      return;
    }

    try {
      setLoading(true);
      await materialService.deleteMaterial(materialId);
      handleRefresh();
    } catch (err) {
      setError(`删除物料失败: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryBadge = (category) => {
    const variants = {
      '棒料': 'default',
      '紧固件': 'secondary',
      '耗材': 'outline',
    };
    return (
      <Badge variant={variants[category] || 'outline'}>
        {category || '其他'}
      </Badge>
    );
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return `¥${price.toFixed(2)}`;
  };

  // Check user permissions
  const canCreate = user && ['admin', 'warehouse_manager', 'engineer'].includes(user.role);
  const canEdit = user && ['admin', 'warehouse_manager', 'engineer'].includes(user.role);

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <Layout activeTab="materials">
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">物料管理</h1>
            <p className="text-muted-foreground">
              管理原材料和物料价格
            </p>
          </div>
          <div className="flex gap-2">
            {lowStockCount > 0 && (
              <Button variant="outline" className="text-orange-600 border-orange-300">
                <AlertTriangle className="h-4 w-4 mr-2" />
                低库存预警 ({lowStockCount})
              </Button>
            )}
            <Button variant="outline" onClick={handleRefresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              刷新
            </Button>
            {canCreate && (
              <>
                <Button variant="outline" onClick={() => setShowImportDialog(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  导入合同
                </Button>
                <Button onClick={handleCreateMaterial}>
                  <Plus className="h-4 w-4 mr-2" />
                  新建物料
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="搜索物料编号、名称或规格..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={categoryFilter || 'all'} onValueChange={handleCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="按类别筛选" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部类别</SelectItem>
                  <SelectItem value="棒料">棒料</SelectItem>
                  <SelectItem value="紧固件">紧固件</SelectItem>
                  <SelectItem value="耗材">耗材</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Materials Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Boxes className="h-5 w-5" />
              物料列表 ({totalItems})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : materials.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                未找到物料。{canCreate && '点击"新建物料"添加。'}
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>物料编号</TableHead>
                      <TableHead>物料名称</TableHead>
                      <TableHead>类别</TableHead>
                      <TableHead>规格</TableHead>
                      <TableHead>单位</TableHead>
                      <TableHead>当前价格</TableHead>
                      <TableHead>状态</TableHead>
                      <TableHead className="text-right">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materials.map((material) => (
                      <TableRow key={material._id}>
                        <TableCell className="font-mono">{material.product_id}</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{material.name}</div>
                            {material.diameter && (
                              <div className="text-sm text-muted-foreground">
                                直径: {material.diameter}mm
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getCategoryBadge(material.material_category)}</TableCell>
                        <TableCell>{material.specification || '-'}</TableCell>
                        <TableCell>{material.unit || '个'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {material.current_price ? (
                              <>
                                <span className="font-medium">
                                  {formatPrice(material.current_price.unit_price_tax_included)}
                                </span>
                                {material.current_price.supplier_name && (
                                  <span className="text-xs text-muted-foreground">
                                    ({material.current_price.supplier_name})
                                  </span>
                                )}
                              </>
                            ) : material.default_unit_cost ? (
                              formatPrice(material.default_unit_cost)
                            ) : (
                              '-'
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {material.is_active !== false ? (
                            <Badge variant="default">有效</Badge>
                          ) : (
                            <Badge variant="destructive">已停用</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewMaterial(material._id)}
                              title="查看详情"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {canEdit && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditMaterial(material._id)}
                                  title="编辑"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/materials/${material._id}?tab=prices`)}
                                  title="价格管理"
                                >
                                  <TrendingUp className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteMaterial(material._id)}
                                  className="text-destructive hover:text-destructive"
                                  title="删除"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center mt-4">
                    <div className="text-sm text-muted-foreground">
                      第 {page} 页 / 共 {totalPages} 页（共 {totalItems} 项）
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        上一页
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        下一页
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Contract Import Dialog */}
        <ContractImportDialog
          open={showImportDialog}
          onClose={() => setShowImportDialog(false)}
          onSuccess={handleImportSuccess}
        />
      </div>
    </Layout>
  );
}

export default MaterialsPage;
